import { create } from 'zustand';

const defaultExplorerQuery = {
    page: 1,
    pageSize: 25,
    search: '',
    sortBy: undefined,
    sortOrder: 'desc',
};

export const useIntegrationConnectionStore = create((set) => ({
    items: [],
    loading: false,
    detailLoading: false,
    error: null,
    columnFilters: {},
    uniqueValues: {},
    providers: [],

    paginationData: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 20,
        hasNextPage: false,
        hasPreviousPage: false,
    },

    searchParams: {
        page: 1,
        pageSize: 20,
        sortBy: 'updatedAt',
        sortOrder: 'desc',
        search: '',
    },

    isFormOpen: false,
    isEditMode: false,
    selectedItem: null,
    deleteDialogOpen: false,
    itemToDelete: null,

    detailOpen: false,
    endpoints: [],
    endpointsLoading: false,

    explorerOpen: false,
    explorerConnection: null,
    explorerEndpointKey: null,
    explorerLoading: false,
    explorerResult: null,
    explorerQuery: { ...defaultExplorerQuery },
    /** @type {{ endpointKey: string, pathParams: object, params: object, label: string }[]} */
    explorerStack: [],
    explorerActiveView: null,
    explorerRowDetail: null,

    workspaceConnection: null,
    workspaceTab: 'overview',
    marketplace: [],
    marketplaceLoading: false,
    providerDefinitions: [],
    providerDefinitionsLoading: false,
    resources: [],
    resourcesLoading: false,
    discoveredSchema: null,
    discoveredFields: [],
    discoveredFingerprint: null,
    discoverWarnings: [],
    discoveredSamples: [],
    discoverLoading: false,
    targetFields: [],
    targetFieldsLoading: false,
    mappings: [],
    mappingsLoading: false,
    selectedMapping: null,
    mappingDetailLoading: false,
    previewRows: [],
    previewMeta: null,
    previewLoading: false,
    runs: [],
    runsLoading: false,
    selectedRun: null,
    runDetailLoading: false,
    runItems: [],
    runItemsLoading: false,
    schedules: [],
    schedulesLoading: false,

    setItems: (items) => set({ items }),
    setLoading: (loading) => set({ loading }),
    setDetailLoading: (detailLoading) => set({ detailLoading }),
    setError: (error) => set({ error }),
    setColumnFilters: (columnFilters) => set({ columnFilters }),
    setUniqueValues: (uniqueValues) => set({ uniqueValues: uniqueValues || {} }),
    clearError: () => set({ error: null }),
    setPaginationData: (paginationData) => set({ paginationData }),
    setSearchParams: (params) => set((state) => ({ searchParams: { ...state.searchParams, ...params } })),
    setProviders: (providers) => set({ providers: providers || [] }),

    openAddForm: () => set({ isFormOpen: true, isEditMode: false, selectedItem: null, error: null }),
    openEditForm: (item) => set({ isFormOpen: true, isEditMode: true, selectedItem: item, error: null }),
    closeForm: () => set({ isFormOpen: false, selectedItem: null, error: null }),

    openDeleteDialog: (item) => set({ deleteDialogOpen: true, itemToDelete: item }),
    closeDeleteDialog: () => set({ deleteDialogOpen: false, itemToDelete: null }),

    openDetail: (item) => set({ detailOpen: true, selectedItem: item }),
    closeDetail: () => set({ detailOpen: false }),

    setEndpoints: (endpoints) => set({ endpoints: endpoints || [] }),
    setEndpointsLoading: (endpointsLoading) => set({ endpointsLoading }),

    openExplorer: (connection, endpointKey = null) => set({
        explorerOpen: true,
        explorerConnection: connection,
        explorerEndpointKey: endpointKey,
        explorerResult: null,
        explorerQuery: { ...defaultExplorerQuery },
        explorerStack: [],
        explorerActiveView: null,
        explorerRowDetail: null,
    }),
    closeExplorer: () => set({
        explorerOpen: false,
        explorerConnection: null,
        explorerEndpointKey: null,
        explorerResult: null,
        explorerStack: [],
        explorerActiveView: null,
        explorerRowDetail: null,
    }),
    setExplorerEndpointKey: (explorerEndpointKey) => set({
        explorerEndpointKey,
        explorerStack: [],
        explorerResult: null,
        explorerActiveView: null,
        explorerRowDetail: null,
        explorerQuery: { ...defaultExplorerQuery },
    }),
    /**
     * Restore Explorer selection from a saved mapping resourceContext without clearing params.
     */
    hydrateExplorerResource: ({ endpointKey, pathParams = {}, queryParams = {}, multiViewKey = null }) => set({
        explorerEndpointKey: endpointKey || null,
        explorerStack: endpointKey
            ? [{
                endpointKey,
                pathParams: pathParams || {},
                params: queryParams || {},
                label: endpointKey,
            }]
            : [],
        explorerActiveView: multiViewKey || null,
        explorerResult: null,
        explorerRowDetail: null,
        explorerQuery: { ...defaultExplorerQuery },
    }),
    setExplorerLoading: (explorerLoading) => set({ explorerLoading }),
    setExplorerResult: (explorerResult) => set({ explorerResult }),
    setExplorerQuery: (params) => set((state) => ({
        explorerQuery: { ...state.explorerQuery, ...params },
    })),
    setExplorerStack: (explorerStack) => set({ explorerStack }),
    pushExplorerFrame: (frame) => set((state) => ({
        explorerStack: [...state.explorerStack, frame],
        explorerEndpointKey: frame.endpointKey,
        explorerActiveView: null,
        explorerRowDetail: null,
        explorerQuery: { ...defaultExplorerQuery },
    })),
    truncateExplorerStack: (index) => set((state) => {
        const next = state.explorerStack.slice(0, index + 1);
        const frame = next[next.length - 1] || null;
        return {
            explorerStack: next,
            explorerEndpointKey: frame?.endpointKey ?? state.explorerEndpointKey,
            explorerActiveView: null,
            explorerRowDetail: null,
            explorerQuery: { ...defaultExplorerQuery },
        };
    }),
    setExplorerActiveView: (explorerActiveView) => set({ explorerActiveView }),
    setExplorerRowDetail: (explorerRowDetail) => set({ explorerRowDetail }),

    setWorkspaceConnection: (workspaceConnection) => set({ workspaceConnection }),
    setWorkspaceTab: (workspaceTab) => set({ workspaceTab }),
    setMarketplace: (marketplace) => set({ marketplace: marketplace || [] }),
    setMarketplaceLoading: (marketplaceLoading) => set({ marketplaceLoading }),
    setProviderDefinitions: (providerDefinitions) => set({
        providerDefinitions: providerDefinitions || [],
    }),
    setProviderDefinitionsLoading: (providerDefinitionsLoading) => set({ providerDefinitionsLoading }),
    setResources: (resources) => set({ resources: resources || [] }),
    setResourcesLoading: (resourcesLoading) => set({ resourcesLoading }),
    setDiscoveredSchema: (discoveredSchema) => set({ discoveredSchema }),
    setDiscoveredFields: (discoveredFields) => set({
        discoveredFields: Array.isArray(discoveredFields) ? discoveredFields : [],
    }),
    setDiscoveredFingerprint: (discoveredFingerprint) => set({ discoveredFingerprint }),
    setDiscoverWarnings: (discoverWarnings) => set({
        discoverWarnings: Array.isArray(discoverWarnings) ? discoverWarnings : [],
    }),
    setDiscoveredSamples: (discoveredSamples) => set({ discoveredSamples: discoveredSamples || [] }),
    setDiscoverLoading: (discoverLoading) => set({ discoverLoading }),
    setTargetFields: (targetFields) => set({
        targetFields: Array.isArray(targetFields) ? targetFields : [],
    }),
    setTargetFieldsLoading: (targetFieldsLoading) => set({ targetFieldsLoading }),
    setMappings: (mappings) => set({ mappings: mappings || [] }),
    setMappingsLoading: (mappingsLoading) => set({ mappingsLoading }),
    setSelectedMapping: (selectedMapping) => set({ selectedMapping }),
    setMappingDetailLoading: (mappingDetailLoading) => set({ mappingDetailLoading }),
    setPreviewRows: (previewRows) => set({ previewRows: previewRows || [] }),
    setPreviewMeta: (previewMeta) => set({ previewMeta }),
    setPreviewLoading: (previewLoading) => set({ previewLoading }),
    setRuns: (runs) => set({ runs: runs || [] }),
    setRunsLoading: (runsLoading) => set({ runsLoading }),
    setSelectedRun: (selectedRun) => set({ selectedRun }),
    setRunDetailLoading: (runDetailLoading) => set({ runDetailLoading }),
    setRunItems: (runItems) => set({ runItems: runItems || [] }),
    setRunItemsLoading: (runItemsLoading) => set({ runItemsLoading }),
    setSchedules: (schedules) => set({ schedules: schedules || [] }),
    setSchedulesLoading: (schedulesLoading) => set({ schedulesLoading }),

    resetWorkspaceState: () => set({
        workspaceConnection: null,
        workspaceTab: 'overview',
        resources: [],
        discoveredSchema: null,
        discoveredFields: [],
        discoveredFingerprint: null,
        discoverWarnings: [],
        discoveredSamples: [],
        targetFields: [],
        mappings: [],
        selectedMapping: null,
        previewRows: [],
        previewMeta: null,
        runs: [],
        selectedRun: null,
        runItems: [],
        schedules: [],
    }),
}));
